import React from 'react';
import { theme, sealColors } from '../../theme';
import { useLanguage } from '../../i18n/useLanguage';
import { SovRadarChart } from './SovRadarChart';
import type {
  SovScoreResult,
  IstSelection,
  SovWeights,
  WorkplaceLevel,
  AggregatedProvider,
  SealDefinition,
  SovObjective,
  DeploymentModel,
  OperationsAnswers,
} from '../../types/workplace';

interface WorkplaceResultsProps {
  istScore: SovScoreResult;
  zielScore: SovScoreResult;
  istSelections: Record<string, IstSelection>;
  zielSelections: Record<string, IstSelection>;
  istOperationsAnswers: OperationsAnswers;
  zielOperationsAnswers: OperationsAnswers;
  sovWeights: SovWeights;
  levels: WorkplaceLevel[];
  aggregatedProviders: AggregatedProvider[];
  sealDefinitions: SealDefinition[];
  sovObjectives: SovObjective[];
  onGenerateReport: () => void;
  onBack: () => void;
  onReset: () => void;
}

const deploymentKeys: Record<DeploymentModel, string> = {
  saas: 'workplace.deployment.saas',
  'hosted-eu': 'workplace.deployment.hostedEu',
  'hosted-non-eu': 'workplace.deployment.hostedNonEu',
  'self-hosted': 'workplace.deployment.selfHosted',
};

export const WorkplaceResults: React.FC<WorkplaceResultsProps> = ({
  istScore,
  zielScore,
  istSelections,
  zielSelections,
  istOperationsAnswers,
  zielOperationsAnswers,
  levels,
  sealDefinitions,
  sovObjectives,
  onGenerateReport,
  onBack,
  onReset,
}) => {
  const { t } = useLanguage();

  const istPct = istScore.overallScore;
  const zielPct = zielScore.overallScore;
  const absDelta = istPct !== null && zielPct !== null ? zielPct - istPct : null;
  const relDelta =
    istPct !== null && zielPct !== null && istPct > 0
      ? ((zielPct - istPct) / istPct) * 100
      : null;

  const sealFor = (level: number | null) =>
    level !== null ? sealDefinitions.find((d) => d.level === level) : undefined;

  const istSeal = sealFor(istScore.sealLevel);
  const zielSeal = sealFor(zielScore.sealLevel);

  // Build sub-point list from new structure
  const subPoints = levels.flatMap((lvl) =>
    lvl.subPoints.map((sp) => ({ id: sp.id, title: sp.title, type: sp.type }))
  );

  // Check if operations scoring has all answers
  const istOpsComplete = istOperationsAnswers.q1 !== null && istOperationsAnswers.q2 !== null &&
    istOperationsAnswers.q3 !== null && istOperationsAnswers.q4 !== null;
  const zielOpsComplete = zielOperationsAnswers.q1 !== null && zielOperationsAnswers.q2 !== null &&
    zielOperationsAnswers.q3 !== null && zielOperationsAnswers.q4 !== null;

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', color: theme.text }}>
        {t('workplace.results.comparison' as any)}
      </h2>

      {/* Score hero: Ist vs Ziel side by side */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <ScoreCard label={t('workplace.results.istScore' as any)} score={istPct} sealLevel={istScore.sealLevel} sealDef={istSeal} />
        <ScoreCard label={t('workplace.results.zielScore' as any)} score={zielPct} sealLevel={zielScore.sealLevel} sealDef={zielSeal} />
      </div>

      {/* Delta */}
      {absDelta !== null && (
        <div style={{ ...cardStyle, marginBottom: 20 }}>
          <span style={{ fontWeight: 700, color: theme.text }}>
            {t('workplace.results.delta' as any)}:
          </span>{' '}
          <span style={{ color: absDelta >= 0 ? theme.secondary : theme.primary, fontWeight: 700 }}>
            {absDelta >= 0 ? '+' : ''}{absDelta.toFixed(1)}%
          </span>
          {relDelta !== null && (
            <span style={{ color: theme.muted, marginLeft: 8, fontSize: 13 }}>
              ({relDelta >= 0 ? '+' : ''}{relDelta.toFixed(1)}% relativ)
            </span>
          )}
        </div>
      )}

      {/* Radar Chart: Ist vs Ziel */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <SovRadarChart istScores={istScore.perObjective} zielScores={zielScore.perObjective} sovObjectives={sovObjectives} />
      </div>

      {/* Per-SOV-Objective comparison */}
      <h3 style={{ margin: '0 0 10px', fontSize: 15, color: theme.text }}>
        {t('workplace.results.perObjective' as any)}
      </h3>
      <div style={{ overflowX: 'auto', marginBottom: 20 }}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${theme.border}` }}>
              <th style={thStyle}>SOV-Objective</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Ist</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Ziel</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Delta</th>
            </tr>
          </thead>
          <tbody>
            {sovObjectives.map((obj) => {
              const ist = istScore.perObjective[obj.id] ?? 0;
              const ziel = zielScore.perObjective[obj.id] ?? 0;
              const d = ziel - ist;
              return (
                <tr key={obj.id} style={{ borderBottom: `1px solid ${theme.cardBg}` }}>
                  <td style={tdStyle}>{obj.name}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{ist.toFixed(1)}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{ziel.toFixed(1)}</td>
                  <td style={{ ...tdStyle, textAlign: 'center', color: d >= 0 ? theme.secondary : theme.primary, fontWeight: 600 }}>
                    {d >= 0 ? '+' : ''}{d.toFixed(1)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Per-Sub-Point breakdown */}
      <h3 style={{ margin: '0 0 10px', fontSize: 15, color: theme.text }}>
        {t('workplace.results.perFeatureGroup' as any)}
      </h3>
      <div style={{ overflowX: 'auto', marginBottom: 20 }}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${theme.border}` }}>
              <th style={thStyle}>Unterpunkt</th>
              <th style={thStyle}>Ist</th>
              <th style={thStyle}>Ziel</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Delta</th>
            </tr>
          </thead>
          <tbody>
            {subPoints.map((sp) => {
              const istSel = istSelections[sp.id];
              const zielSel = zielSelections[sp.id];
              const istFg = istScore.perFeatureGroup[sp.id];
              const zielFg = zielScore.perFeatureGroup[sp.id];
              const istS = istFg?.sovScore;
              const zielS = zielFg?.sovScore;
              const d = istS !== null && istS !== undefined && zielS !== null && zielS !== undefined ? zielS - istS : null;

              const isOps = sp.type === 'operations-scoring';

              return (
                <tr key={sp.id} style={{ borderBottom: `1px solid ${theme.cardBg}` }}>
                  <td style={tdStyle}>{sp.title}</td>
                  <td style={tdStyle}>
                    {isOps ? (
                      <OpsCell complete={istOpsComplete} score={istS} />
                    ) : (
                      <FgCell sel={istSel} score={istS} t={t} />
                    )}
                  </td>
                  <td style={tdStyle}>
                    {isOps ? (
                      <OpsCell complete={zielOpsComplete} score={zielS} />
                    ) : (
                      <FgCell sel={zielSel} score={zielS} t={t} />
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, color: d !== null ? (d >= 0 ? theme.secondary : theme.primary) : theme.muted }}>
                    {d !== null ? `${d >= 0 ? '+' : ''}${d.toFixed(1)}%` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 24, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onBack} style={outlineBtn}>
            {t('workplace.results.backToBuilder' as any)}
          </button>
          <button onClick={onReset} style={{ ...outlineBtn, color: theme.primary, borderColor: theme.primary }}>
            {t('workplace.results.reset' as any)}
          </button>
        </div>
        <button onClick={onGenerateReport} style={primaryBtn}>
          {t('workplace.results.generateReport' as any)}
        </button>
      </div>
    </div>
  );
};

/* ---------- helpers ---------- */

const ScoreCard: React.FC<{
  label: string; score: number | null; sealLevel: number | null; sealDef?: SealDefinition;
}> = ({ label, score, sealLevel, sealDef }) => (
  <div style={{
    flex: '1 1 200px', padding: 20, borderRadius: 10,
    background: sealLevel !== null ? (sealColors[sealLevel] || theme.cardBg) : theme.cardBg,
    color: sealLevel !== null ? '#fff' : theme.text,
  }}>
    <p style={{ margin: '0 0 4px', fontSize: 13, opacity: 0.85 }}>{label}</p>
    <span style={{ fontSize: 36, fontWeight: 800 }}>{score !== null ? `${score.toFixed(1)}%` : '—'}</span>
    {sealDef && <p style={{ margin: '6px 0 0', fontSize: 13, opacity: 0.9 }}>SEAL {sealLevel} — {sealDef.name}</p>}
  </div>
);

const FgCell: React.FC<{ sel?: IstSelection; score?: number | null; t: (key: any) => string }> = ({ sel, score, t }) => {
  if (!sel) return <span style={{ color: theme.muted }}>—</span>;
  return (
    <span style={{ fontSize: 13 }}>
      {sel.provider} · {t(deploymentKeys[sel.deployment] as any)}
      {score != null && <span style={{ marginLeft: 6, fontWeight: 600 }}>{score.toFixed(1)}%</span>}
    </span>
  );
};

const OpsCell: React.FC<{ complete: boolean; score?: number | null }> = ({ complete, score }) => {
  if (!complete) return <span style={{ color: theme.muted }}>—</span>;
  return (
    <span style={{ fontSize: 13, fontWeight: 600 }}>
      {score != null ? `${score.toFixed(1)}%` : '—'}
    </span>
  );
};

/* ---------- styles ---------- */
const cardStyle: React.CSSProperties = { border: `1px solid ${theme.border}`, borderRadius: 8, padding: 14, background: theme.cardBg };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle: React.CSSProperties = { textAlign: 'left', padding: '8px 6px', fontWeight: 600, color: theme.muted };
const tdStyle: React.CSSProperties = { padding: '8px 6px', color: theme.text };
const outlineBtn: React.CSSProperties = { padding: '10px 20px', borderRadius: 6, fontWeight: 700, fontSize: 14, border: `1px solid ${theme.border}`, background: '#fff', cursor: 'pointer', color: theme.text };
const primaryBtn: React.CSSProperties = { padding: '10px 24px', borderRadius: 6, fontWeight: 700, fontSize: 14, border: 'none', background: theme.primary, color: '#fff', cursor: 'pointer' };
